<template>
	<div :id="wrapperId" class="simple-typeahead" style="position:relative">
		<input
			:id="inputId"
			class="form-control"
			:class="{'form-control-sm': small}"
			style="padding-left:30px"
			type="text"
			:placeholder="placeholder"
			v-model="input"
			@input="onInput"
			@focus="onFocus"
			@blur="onBlur"
			@keydown.down.prevent="onArrowDown"
			@keydown.up.prevent="onArrowUp"
			@keydown.enter.tab.prevent="selectCurrentSelection"
			autocomplete="off"
			ref="input"
		/>
		<div v-show="isListVisible" class="simple-typeahead-list" ref="list">
			<div class="simple-typeahead-list-header" v-if="$slots['list-header']"><slot name="list-header"></slot></div>
			<div
				class="simple-typeahead-list-item"
				:class="{ 'simple-typeahead-list-item-active': currentSelectionIndex == index }"
				v-for="(item, index) in filteredItems"
				:key="index"
				@mousedown.prevent
				@click="selectItem(item)"
				@mouseenter="currentSelectionIndex = index"
			>
				<span class="simple-typeahead-list-item-text" :data-text="itemProjection(item)" v-if="$slots['list-item-text']"
					><slot name="list-item-text" :item="item" :itemProjection="itemProjection" :boldMatchText="boldMatchText"></slot
				></span>
				<span class="simple-typeahead-list-item-text" :data-text="itemProjection(item)" v-html="boldMatchText(itemProjection(item))" v-else></span>
			</div>
			<div class="simple-typeahead-list-footer" v-if="$slots['list-footer']"><slot name="list-footer"></slot></div>
		</div>
		<span style="position:absolute; left:7px; " :style="small ? 'top: 5px' : 'top: 10px'" v-if="busy" >
			<font-awesome-icon icon="fa-solid fa-spinner" class="fa-svg-spin" />
		</span>
		<span style="position:absolute; left:7px; " :style="small ? 'top: 5px' : 'top: 10px'" v-if="!busy" >
			<font-awesome-icon icon="fa-solid fa-search" class="text-muted"/>
		</span>

		<!-- <i class="fa fa-spin fa-spinner font-size-16" v-if="busy" style="position:absolute; left: 7px; top: 10px"></i>
		<i class="fa fa-search text-muted font-size-14" v-if="!busy" style="position:absolute; left: 7px; top: 12px"></i> -->
	</div>
</template>

<script>
	import { defineComponent } from 'vue';

	export default /*#__PURE__*/ defineComponent({
		name: 'Vue3SimpleTypeahead',
		emits: ['onInput', 'onFocus', 'onBlur', 'selectItem'],
		props: {
			busy: {
				type: Boolean
			},
			id: {
				type: String,
			},
			placeholder: {
				type: String,
				default: '',
			},
			items: {
				type: Array,
				required: true,
			},
			defaultItem: {
				default: null,
			},
			itemProjection: {
				type: Function,
				default(item) {
					return item;
				},
			},
			small: {
				type: Boolean,
			},
			minInputLength: {
				type: Number,
				default: 2,
				validator: (prop) => {
					return prop >= 0;
				},
			},
		},
		mounted() {
			if (this.defaultItem !== undefined && this.defaultItem !== null) {
				this.selectItem(this.defaultItem);
			}
		},
		data() {
			return {
				inputId: this.id || `simple_typeahead_${(Math.random() * 1000).toFixed()}`,
				input: '',
				isInputFocused: false,
				currentSelectionIndex: 0,
			};
		},
		methods: {
			onInput() {
				if (this.isListVisible && this.currentSelectionIndex >= this.filteredItems.length) {
					this.currentSelectionIndex = (this.filteredItems.length || 1) - 1;
				}
				this.$emit('onInput', { input: this.input, items: this.filteredItems });
			},
			onFocus() {
				this.isInputFocused = true;
				this.$emit('onFocus', { input: this.input, items: this.filteredItems });
			},
			onBlur() {
				this.isInputFocused = false;
				this.$emit('onBlur', { input: this.input, items: this.filteredItems });
			},
			onArrowDown() {
				if (this.isListVisible && this.currentSelectionIndex < this.filteredItems.length - 1) {
					this.currentSelectionIndex++;
				}
				this.scrollSelectionIntoView();
			},
			onArrowUp() {
				if (this.isListVisible && this.currentSelectionIndex > 0) {
					this.currentSelectionIndex--;
				}
				this.scrollSelectionIntoView();
			},
			scrollSelectionIntoView() {
				setTimeout(() => {
					const list_node = document.querySelector(`#${this.wrapperId} .simple-typeahead-list`);
					const active_node = document.querySelector(`#${this.wrapperId} .simple-typeahead-list-item.simple-typeahead-list-item-active`);

					if (!(active_node.offsetTop >= list_node.scrollTop && active_node.offsetTop + active_node.offsetHeight < list_node.scrollTop + list_node.offsetHeight)) {
						let scroll_to = 0;
						if (active_node.offsetTop > list_node.scrollTop) {
							scroll_to = active_node.offsetTop + active_node.offsetHeight - list_node.offsetHeight;
						} else if (active_node.offsetTop < list_node.scrollTop) {
							scroll_to = active_node.offsetTop;
						}

						list_node.scrollTo(0, scroll_to);
					}
				});
			},
			selectCurrentSelection() {
				if (this.currentSelection) {
					this.selectItem(this.currentSelection);
				}
			},
			selectItem(item) {
				this.input = this.itemProjection(item);
				this.currentSelectionIndex = 0;
				document.getElementById(this.inputId).blur();
				this.$emit('selectItem', item);
			},
			escapeRegExp(string) {
				return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			},
			boldMatchText(text) {
				const regexp = new RegExp(`(${this.escapeRegExp(this.input)})`, 'ig');
				return text.replace(regexp, '<strong>$1</strong>');
			},
			stickList() {
				if(!this.$refs.input && this.$t) {
					clearInterval(this.$t);
					this.$t = null;
					return;
				}
				const viewportPos = this.$refs.input.getBoundingClientRect();
				let top = viewportPos.y + viewportPos.height;
				let height = 300;
				const bottomBorder = top + height;
				if(bottomBorder > window.innerHeight) {
					height = height - (bottomBorder - window.innerHeight);
					if(height < 200) {
						height = 200;
						top =  viewportPos.y - height;
					}
				}
				let width = viewportPos.width;
				if(width < 250) {
					width = 250;
				}
				this.$refs.list.style.top = (top) + 'px';
				this.$refs.list.style.left = viewportPos.x + 'px';
				this.$refs.list.style.width = width + 'px';
				this.$refs.list.style.height = height + 'px';
			}
			
		},
		computed: {
			wrapperId() {
				return `${this.inputId}_wrapper`;
			},
			filteredItems() {
				if(!this.input) {
					return this.items.filter((item) => this.itemProjection(item));
				}
				const regexp = new RegExp(this.escapeRegExp(this.input), 'i');

				return this.items.filter((item) => this.itemProjection(item).match(regexp));
			},
			isListVisible() {
				return this.isInputFocused && this.filteredItems.length;
			},
			currentSelection() {
				return this.isListVisible && this.currentSelectionIndex < this.filteredItems.length ? this.filteredItems[this.currentSelectionIndex] : undefined;
			},
		},
		watch: {
			isListVisible(newValue) {
				console.log('Window.innerHeight', window.innerHeight);
				if(newValue) {
					this.stickList();
					this.$t = window.setInterval(() => {
						this.stickList();
					}, 50);
				} else {
					if(this.$t) {
						clearInterval(this.$t);
						this.$t = null;
					}
				}
			}
		}
	});
</script>

<style scoped>
	.simple-typeahead {
		position: relative;
		width: 100%;
	}
	.simple-typeahead > input {
		margin-bottom: 0;
	}
	.simple-typeahead .simple-typeahead-list {
		position: fixed;
		width:100px;
		border: none;
		max-height: 500px;
		overflow-y: auto;
		border-bottom: 0.1rem solid #d1d1d1;
		z-index: 9;

	}
	.simple-typeahead .simple-typeahead-list .simple-typeahead-list-header {
		background-color: #fafafa;
		padding: 0.6rem 1rem;
		border-bottom: 0.1rem solid #d1d1d1;
		border-left: 0.1rem solid #d1d1d1;
		border-right: 0.1rem solid #d1d1d1;
	}
	.simple-typeahead .simple-typeahead-list .simple-typeahead-list-footer {
		background-color: #fafafa;
		padding: 0.6rem 1rem;
		border-left: 0.1rem solid #d1d1d1;
		border-right: 0.1rem solid #d1d1d1;
	}
	.simple-typeahead .simple-typeahead-list .simple-typeahead-list-item {
		cursor: pointer;
		background-color: #fafafa;
		padding: 0.6rem 1rem;
		border-bottom: 0.1rem solid #d1d1d1;
		border-left: 0.1rem solid #d1d1d1;
		border-right: 0.1rem solid #d1d1d1;
	}

	.simple-typeahead .simple-typeahead-list .simple-typeahead-list-item:last-child {
		border-bottom: none;
	}

	.simple-typeahead .simple-typeahead-list .simple-typeahead-list-item.simple-typeahead-list-item-active {
		background-color: #e1e1e1;
	}
</style>